import React from "react";
import EmployeeProfileTabHeader from "./EmployeeProfileTabHeader";
import EmployeeProfileSectionCard from "./EmployeeProfileSectionCard";
import EmployeeProfileEmptyState from "./EmployeeProfileEmptyState";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import LoadSpinner from "../../../../../utilities/LoadSpinner";
import { formatDateReadable, getExpirationStatus } from "../../utilities/helpers";
import { getRightworkAPI, postRightworkAPI } from "../../utilities/requests";
import {
  API_get_earned_badges,
  API_revoke_earned_badge,
  API_remove_record_from_profile,
} from "../../../../../constants";
import {
  LMS_DATA_TYPES,
  LMS_RECORD_LABELS,
  getDateLabel,
  recordDateText,
  RowShell,
  IconThumb,
} from "./profileRecordHelpers";
import FormSubmissionReviewModal from "../../../training_overview/FormSubmissionReviewModal";
import RoleplayAttemptReviewModal from "../../../training_overview/RoleplayAttemptReviewModal";
import QuizReviewModal from "../../../create_training_content/QuizReviewModal";

// The employee's training story in one tab: Badges (credentials earned by
// completing tracks - lms_info.EarnedBadge) on top, Training Records (the
// data-backed evidence: quiz results, checkpoint sign-offs, manager inputs,
// form submissions, roleplay practice) beneath. Deliberately separate from
// the Documents tab, which keeps only file-backed items - badges and records
// are training outcomes, not compliance paperwork. Managers can revoke a
// wrongly-earned badge and un-pin records; employees see their own read-only
// (isSelfView).

function EmployeeProfileTrainingTab(props) {
  const [earnedBadges, setEarnedBadges] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  // Form submission read-only viewer
  const [formReview, setFormReview] = React.useState(null);
  // Roleplay scorecard/transcript viewer (self-fetching, same as Records tab)
  const [roleplayReview, setRoleplayReview] = React.useState(null);
  // Quiz per-question review (self-fetching; get_quiz_attempt_detail allows
  // the attempt owner or a company manager, so both profile views work)
  const [quizReview, setQuizReview] = React.useState(null);

  const isSelfView = !!props.isSelfView;

  // Bumped after a revoke to refetch for the current subject.
  const [badgesRefreshKey, setBadgesRefreshKey] = React.useState(0);

  // Reset on subject change and drop in-flight responses from the previous
  // subject via the cleanup flag - if this component is ever rebound to
  // another employee without unmounting, a slow fetch must not paint the
  // prior employee's badges onto the current profile.
  React.useEffect(() => {
    let active = true;
    setEarnedBadges(null);
    const fetchEarnedBadges = async () => {
      if (!props.homeStore || !props.stm_id) return;
      setIsLoading(true);
      try {
        const res = await getRightworkAPI(API_get_earned_badges, {
          store_id: props.homeStore,
          store_team_member_id: props.stm_id,
        });
        if (active && res.status === 200) {
          setEarnedBadges(res.data?.redux?.payload?.earned_badges || []);
        }
      } catch (err) {
        console.error("Failed to fetch earned badges:", err);
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };
    fetchEarnedBadges();
    return () => {
      active = false;
    };
  }, [props.homeStore, props.stm_id, badgesRefreshKey]);

  const handleRevoke = async (badge) => {
    if (
      !window.confirm(
        `Remove the ${badge.badge_name} badge? Re-completing the training will award it again.`
      )
    ) {
      return;
    }
    try {
      await postRightworkAPI(API_revoke_earned_badge, {
        store_id: props.homeStore,
        // Binds the revoke to this profile's employee server-side (the
        // backend rejects a badge uuid that isn't theirs).
        store_team_member_id: props.stm_id,
        earned_badge_uuid: badge.uuid,
      });
      toast.success("Badge removed");
      setBadgesRefreshKey((k) => k + 1);
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Failed to remove badge"
      );
    }
  };

  // Training records ride the same combined array as the Documents tab
  // (certificates + LMS records); this tab keeps only the data-backed types.
  const trainingRecords = React.useMemo(() => {
    const records = (props.certificateData || []).filter((record) =>
      LMS_DATA_TYPES.includes(record.type)
    );
    records.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
    return records;
  }, [props.certificateData]);

  // Non-destructive: un-pins the record from the profile (keeps the underlying
  // training completion + its Records-tab data intact).
  const handleRemoveLMSRecord = async (recordUuid, recordType) => {
    try {
      const response = await postRightworkAPI(API_remove_record_from_profile, {
        record_type: recordType,
        completion_uuid: recordUuid,
        company_id: props.homeCompany,
      });

      if (response.status === 200) {
        toast.success("Removed from profile");
        if (props.onRefresh) {
          props.onRefresh(false, props.stm_id);
        }
      }
    } catch (error) {
      console.error("Error removing record from profile:", error);
      toast.error("Failed to remove from profile");
    }
  };

  const renderStatusPill = (badge) => {
    const { status } = getExpirationStatus(badge.expires_at);
    if (status === "expired") {
      return (
        <span className="shrink-0 rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[11px] font-semibold text-red-700">
          Expired {formatDateReadable(badge.expires_at)}
        </span>
      );
    }
    if (status === "expiring") {
      return (
        <span className="shrink-0 rounded-full bg-amber-50 border border-amber-300 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
          Expires {formatDateReadable(badge.expires_at)}
        </span>
      );
    }
    if (status === "valid") {
      return (
        <span className="shrink-0 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
          Valid until {formatDateReadable(badge.expires_at)}
        </span>
      );
    }
    return null;
  };

  // Compact inline chips per training record type.
  const trainingChips = (record) => {
    switch (record.type) {
      case "quiz_result":
        return (
          <>
            <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
              Passed
            </span>
            {record.score !== null && record.score !== undefined && (
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                {Math.round(record.score)}%
              </span>
            )}
          </>
        );
      case "roleplay_result":
        return record.has_scorecard ? (
          <>
            {record.met_count > 0 && (
              <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                {record.met_count} met
              </span>
            )}
            {record.partial_count > 0 && (
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                {record.partial_count} partial
              </span>
            )}
            {record.missed_count > 0 && (
              <span className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                {record.missed_count} missed
              </span>
            )}
          </>
        ) : null;
      case "form_submission":
        return (
          <>
            {record.score !== null && record.score !== undefined && (
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                Score {record.score}
              </span>
            )}
            {record.is_passed === true && (
              <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                Passed
              </span>
            )}
            {record.is_passed === false && (
              <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                Not passed
              </span>
            )}
            {record.grading_status === "pending" && (
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                Awaiting grade
              </span>
            )}
          </>
        );
      default:
        return null;
    }
  };

  const trainingSubline = (record) => {
    const base = `${LMS_RECORD_LABELS[record.type]} · ${getDateLabel(record.type)} ${recordDateText(record)}`;
    switch (record.type) {
      case "checkpoint_signoff":
        return record.approved_by ? `${base} by ${record.approved_by}` : `${base} (manager verified)`;
      case "manager_input":
        return record.input_value ? `${base} · "${record.input_value}"` : base;
      case "roleplay_result":
        return record.has_scorecard ? base : `${base} · Conversation completed`;
      default:
        return base;
    }
  };

  const renderTrainingRow = (record) => (
    <RowShell
      key={record.uuid}
      leading={<IconThumb type={record.type} />}
      actions={
        <>
          {/* attempt_uuid is null for legacy passes that predate QuizAttempt
              tracking - no answers exist to show, so no button. */}
          {record.type === "quiz_result" && record.attempt_uuid && (
            <Tooltip title="View answers">
              <IconButton size="small" onClick={() => setQuizReview(record)}>
                <VisibilityIcon style={{ fontSize: 17 }} className="text-slate-500" />
              </IconButton>
            </Tooltip>
          )}
          {record.type === "form_submission" && (
            <Tooltip title="View responses">
              <IconButton size="small" onClick={() => setFormReview(record)}>
                <VisibilityIcon style={{ fontSize: 17 }} className="text-slate-500" />
              </IconButton>
            </Tooltip>
          )}
          {record.type === "roleplay_result" && (
            <Tooltip title="View feedback and conversation">
              <IconButton size="small" onClick={() => setRoleplayReview(record)}>
                <VisibilityIcon style={{ fontSize: 17 }} className="text-slate-500" />
              </IconButton>
            </Tooltip>
          )}
          {!isSelfView && (
            <Tooltip title="Remove from profile">
              <IconButton
                size="small"
                onClick={() => {
                  if (
                    !window.confirm(
                      "Remove this record from the employee's profile? The training completion itself is kept."
                    )
                  ) {
                    return;
                  }
                  handleRemoveLMSRecord(record.uuid, record.type);
                }}
              >
                <DeleteIcon style={{ fontSize: 16 }} className="text-red-500" />
              </IconButton>
            </Tooltip>
          )}
        </>
      }
    >
      <div className="flex items-center gap-2 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">
          {record.title || LMS_RECORD_LABELS[record.type]}
        </p>
        {trainingChips(record)}
      </div>
      <p className="text-xs text-slate-500 truncate mt-0.5">{trainingSubline(record)}</p>
    </RowShell>
  );

  // Simple non-collapsible section header (the Documents tab's collapsible
  // Section is overkill for two short sections).
  const SectionHeader = ({ icon: SectionIcon, title, count }) => (
    <div className="flex items-center gap-2 px-1 py-1.5 mb-1">
      <SectionIcon style={{ fontSize: 17 }} className="text-slate-500" />
      <span className="text-sm font-semibold text-slate-700">{title}</span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
        {count}
      </span>
    </div>
  );

  const hasBadges = !!earnedBadges && earnedBadges.length > 0;
  const hasRecords = trainingRecords.length > 0;

  return (
    <div>
      <EmployeeProfileTabHeader
        icon={SchoolOutlinedIcon}
        title="Training"
        subtitle="Badges earned and saved training records"
      />

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <LoadSpinner />
        </div>
      ) : !hasBadges && !hasRecords ? (
        <EmployeeProfileSectionCard bodyClassName="">
          <EmployeeProfileEmptyState
            icon={SchoolOutlinedIcon}
            title="No training activity yet"
            description="Badges and training records earned by completing training will appear here."
          />
        </EmployeeProfileSectionCard>
      ) : (
        <>
          {hasBadges && (
            <div className="mb-5">
              <SectionHeader
                icon={WorkspacePremiumIcon}
                title="Badges"
                count={earnedBadges.length}
              />
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-4">
                {earnedBadges.map((badge) => (
                  <div
                    key={badge.uuid}
                    className="rounded-xl border border-slate-200 bg-white p-5"
                  >
                    <div className="flex items-start gap-4">
                      {/* Plain seal in the badge's own color - no ring/halo, which
                          read as a clashing border around the chosen accent. */}
                      <div
                        className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center shadow-sm ${badge.color || "bg-violet-600"}`}
                      >
                        <WorkspacePremiumIcon
                          className="text-white drop-shadow"
                          style={{ fontSize: 22 }}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {badge.badge_name}
                          </p>
                          {renderStatusPill(badge)}
                        </div>
                        {badge.source_learning_track_name && (
                          <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                            <SchoolOutlinedIcon style={{ fontSize: 14 }} />
                            <span className="truncate">
                              Earned via {badge.source_learning_track_name}
                            </span>
                          </p>
                        )}
                        <p className="mt-1 text-xs text-slate-400">
                          {/* earned_at is epoch ms; formatDateReadable expects a
                              YYYY-MM-DD string, so format directly. */}
                          Earned{" "}
                          {new Date(badge.earned_at).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      {/* Pinned to the card's top-right edge, matching where
                          record rows keep their actions. */}
                      {!isSelfView && (
                        <Tooltip title="Remove badge">
                          <IconButton
                            size="small"
                            className="shrink-0 -mt-1 -mr-2"
                            onClick={() => handleRevoke(badge)}
                          >
                            <DeleteIcon
                              style={{ fontSize: 16 }}
                              className="text-red-500"
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasRecords && (
            <div className="mb-5">
              <SectionHeader
                icon={SchoolOutlinedIcon}
                title="Records"
                count={trainingRecords.length}
              />
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100 overflow-hidden">
                {trainingRecords.map(renderTrainingRow)}
              </div>
            </div>
          )}
        </>
      )}

      {/* Read-only form submission viewer */}
      <FormSubmissionReviewModal
        open={!!formReview}
        onClose={() => setFormReview(null)}
        record={
          formReview
            ? {
                name: formReview.title,
                fields: formReview.fields || [],
                scoring_enabled: !!formReview.scoring_enabled,
              }
            : null
        }
        submission={
          formReview
            ? {
                fields: formReview.fields || [],
                responses: formReview.responses || {},
                score: formReview.score,
                is_passed: formReview.is_passed,
                date_completed: formReview.created_at,
              }
            : null
        }
      />

      {quizReview && (
        <QuizReviewModal
          open={!!quizReview}
          // QuizReviewModal's close prop is handleClose, not onClose.
          handleClose={() => setQuizReview(null)}
          homeCompany={props.homeCompany}
          attemptUuid={quizReview.attempt_uuid}
        />
      )}

      {roleplayReview && (
        <RoleplayAttemptReviewModal
          open={!!roleplayReview}
          onClose={() => setRoleplayReview(null)}
          homeCompany={props.homeCompany}
          attemptUuid={roleplayReview.uuid}
          // Whose attempt to load: without this the backend read helper
          // defaults to request.user, so a MANAGER viewing an employee's
          // profile would look up the attempt under their own user id and
          // the modal would show a load error. Deliberately OMITTED on
          // self-view: the helper requires manager permission whenever the
          // param is present (no self exception), so an employee passing
          // their own stm_id would get "Invalid access" - omitting it
          // defaults to request.user, which is them.
          storeTeamMemberId={isSelfView ? undefined : props.stm_id}
        />
      )}
    </div>
  );
}

export default EmployeeProfileTrainingTab;
